/**
 * Map sc2reader unit/building type names onto sc2Data costs and minimap size tiers.
 */
import { STRUCTURES, UNITS } from './sc2Data';

/** sc2reader PascalCase / variant names → sc2Data snake_case ids */
const NAME_ALIASES = {
  // Terran morphs / modes
  HellionTank: 'hellbat',
  BattleHellion: 'hellion',
  VikingFighter: 'viking',
  VikingAssault: 'viking',
  LiberatorAG: 'liberator',
  SiegeTankSieged: 'siege_tank',
  WidowMineBurrowed: 'widow_mine',
  ThorAP: 'thor',
  OrbitalCommand: 'command_center',
  OrbitalCommandFlying: 'command_center',
  PlanetaryFortress: 'command_center',
  CommandCenterFlying: 'command_center',
  BarracksFlying: 'barracks',
  FactoryFlying: 'factory',
  StarportFlying: 'starport',
  SupplyDepotLowered: 'supply_depot',
  BarracksTechLab: 'tech_lab',
  FactoryTechLab: 'tech_lab',
  StarportTechLab: 'tech_lab',
  BarracksReactor: 'reactor',
  FactoryReactor: 'reactor',
  StarportReactor: 'reactor',
  // Protoss
  WarpGate: 'gateway',
  WarpPrismPhasing: 'warp_prism',
  ObserverSiegeMode: 'observer',
  // Zerg morphs / modes
  Overseer: 'overlord',
  OverseerOversightMode: 'overlord',
  OverlordTransport: 'overlord',
  ZerglingBurrowed: 'zergling',
  BanelingBurrowed: 'baneling',
  RoachBurrowed: 'roach',
  HydraliskBurrowed: 'hydralisk',
  InfestorBurrowed: 'infestor',
  SwarmHostBurrowed: 'swarm_host',
  UltraliskBurrowed: 'ultralisk',
  LurkerMP: 'lurker',
  LurkerMPBurrowed: 'lurker',
  SpineCrawlerUprooted: 'spine_crawler',
  SporeCrawlerUprooted: 'spore_crawler',
};

/** Ephemeral / non-composition entities — skip cost and usually composition */
const SKIP_COST_NAMES = new Set([
  'KD8Charge',
  'AutoTurret',
  'PointDefenseDrone',
  'Interceptor',
  'LocustMP',
  'LocustMPFlying',
  'Broodling',
  'BroodlingEscort',
  'InfestedTerransEgg',
  'Changeling',
  'ChangelingMarine',
  'ChangelingMarineShield',
  'ChangelingZealot',
  'ChangelingZergling',
  'ChangelingZerglingWings',
]);

const LARGE_BUILDING_TOKENS = [
  'nexus',
  'commandcenter',
  'orbitalcommand',
  'planetaryfortress',
  'hatchery',
  'lair',
  'hive',
];

const SMALL_BUILDING_TOKENS = [
  'supplydepot',
  'pylon',
  'photoncannon',
  'shieldbattery',
  'sensortower',
  'missileturret',
  'spinecrawler',
  'sporecrawler',
  'creeptumor',
  'autobuilding',
];

/**
 * PascalCase / mixed replay name → snake_case id candidate.
 * @param {string} name
 * @returns {string}
 */
export function normalizeReplayName(name) {
  if (!name) return '';
  if (NAME_ALIASES[name]) return NAME_ALIASES[name];
  return String(name)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Resolve sc2Data unit or structure entry for a replay type name.
 * @param {string} name
 * @returns {{ id: string, minerals: number, gas: number } | null}
 */
export function lookupUnitOrStructure(name) {
  if (!name || SKIP_COST_NAMES.has(name)) return null;
  const id = normalizeReplayName(name);
  if (!id) return null;
  const unit = UNITS[id];
  if (unit) {
    return { id: unit.id, minerals: unit.minerals || 0, gas: unit.gas || 0 };
  }
  const structure = STRUCTURES[id];
  if (structure) {
    return {
      id: structure.id,
      minerals: structure.minerals || 0,
      gas: structure.gas || 0,
    };
  }
  return null;
}

/**
 * @param {string} name
 * @param {string} [kind]
 * @returns {{ minerals: number, gas: number }}
 */
export function costForEntity(name, kind) {
  if (kind === 'mineral' || kind === 'gas') {
    return { minerals: 0, gas: 0 };
  }
  const entry = lookupUnitOrStructure(name);
  if (!entry) return { minerals: 0, gas: 0 };
  return { minerals: entry.minerals, gas: entry.gas };
}

/**
 * Building footprint tier for minimap squares.
 * @param {string} name
 * @returns {'s' | 'm' | 'l'}
 */
export function buildingSizeTier(name) {
  const compact = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!compact) return 'm';
  if (LARGE_BUILDING_TOKENS.some((t) => compact.includes(t))) return 'l';
  if (SMALL_BUILDING_TOKENS.some((t) => compact.includes(t))) return 's';
  return 'm';
}

/**
 * SC2 placement footprint edge length in map cells (Camera stage, D67).
 * Tracker (x,y) is the footprint center — even sizes sit on cell corners.
 * @param {string} name
 * @returns {number}
 */
export function buildingFootprintCells(name) {
  const tier = buildingSizeTier(name);
  if (tier === 'l') return 5;
  if (tier === 's') return 2;
  return 3;
}

/** Curated two-letter labels for common buildings on the Camera stage. */
const BUILDING_SHORT_LABELS = {
  CommandCenter: 'CC',
  OrbitalCommand: 'OC',
  PlanetaryFortress: 'PF',
  SupplyDepot: 'SD',
  SupplyDepotLowered: 'SD',
  Barracks: 'Ra',
  Factory: 'Fa',
  Starport: 'Sp',
  EngineeringBay: 'EB',
  Armory: 'Ar',
  Bunker: 'Bu',
  MissileTurret: 'MT',
  SensorTower: 'ST',
  Refinery: 'Rf',
  RichRefinery: 'Rf',
  GhostAcademy: 'GA',
  FusionCore: 'FC',
  TechLab: 'TL',
  BarracksTechLab: 'TL',
  FactoryTechLab: 'TL',
  StarportTechLab: 'TL',
  Reactor: 'Rx',
  BarracksReactor: 'Rx',
  FactoryReactor: 'Rx',
  StarportReactor: 'Rx',
  Nexus: 'Nx',
  Pylon: 'Py',
  Gateway: 'Gw',
  WarpGate: 'WG',
  Forge: 'Fo',
  CyberneticsCore: 'Cy',
  TwilightCouncil: 'TC',
  TemplarArchives: 'TA',
  DarkShrine: 'DS',
  RoboticsFacility: 'Ro',
  RoboticsBay: 'RB',
  Stargate: 'Sg',
  FleetBeacon: 'FB',
  PhotonCannon: 'PC',
  ShieldBattery: 'SB',
  Assimilator: 'As',
  RichAssimilator: 'As',
  Hatchery: 'Ha',
  Lair: 'La',
  Hive: 'Hi',
  SpawningPool: 'SP',
  EvolutionChamber: 'EC',
  RoachWarren: 'RW',
  BanelingNest: 'BN',
  HydraliskDen: 'HD',
  InfestationPit: 'IP',
  Spire: 'Si',
  GreaterSpire: 'GS',
  UltraliskCavern: 'UC',
  NydusNetwork: 'NN',
  NydusCanal: 'NW',
  SpineCrawler: 'Sc',
  SporeCrawler: 'Sc',
  Extractor: 'Ex',
  RichExtractor: 'Ex',
  CreepTumor: 'CT',
  CreepTumorBurrowed: 'CT',
  CreepTumorQueen: 'CT',
};

/**
 * Two-letter Camera label for a building type name (D68).
 * @param {string} name
 * @returns {string}
 */
export function buildingShortLabel(name) {
  if (!name) return '??';
  if (BUILDING_SHORT_LABELS[name]) return BUILDING_SHORT_LABELS[name];
  const parts = String(name)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return String(name).slice(0, 2);
}

/**
 * Aggregate army cost for a list of live entities (kind === 'army' only).
 * @param {Array<{ kind?: string, name?: string }>} entities
 * @returns {{ minerals: number, gas: number, total: number }}
 */
export function armyCostForEntities(entities) {
  let minerals = 0;
  let gas = 0;
  for (const entity of entities || []) {
    if (entity.kind !== 'army') continue;
    const cost = costForEntity(entity.name, entity.kind);
    minerals += cost.minerals;
    gas += cost.gas;
  }
  return { minerals, gas, total: minerals + gas };
}

/**
 * Count entities by name for a kind filter.
 * @param {Array<{ kind?: string, name?: string }>} entities
 * @param {string} kind
 * @returns {Array<{ name: string, count: number }>}
 */
export function compositionByKind(entities, kind) {
  const counts = new Map();
  for (const entity of entities || []) {
    if (entity.kind !== kind) continue;
    const name = entity.name || 'Unknown';
    if (SKIP_COST_NAMES.has(name)) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
